import React, { useState, useEffect } from 'react';
import {
  RefreshCw, Clock, Play, CheckCircle, XCircle,
  ChevronDown, ChevronRight, Trash2, Download, StopCircle, AlertTriangle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import apiService from '../services/api';

const JobStatusPanel = ({ currentJobId, onJobSelect }) => {
  const [jobs, setJobs] = useState({ waiting: [], active: [], completed: [], failed: [] });
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    active: true,
    waiting: true,
    completed: true,
    failed: true
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [runCleanupOnCancel, setRunCleanupOnCancel] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllJobs();
      if (response.success) {
        setJobs(response.jobs);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId, e) => {
    e.stopPropagation();

    if (!window.confirm('Delete this job?')) return;

    try {
      await apiService.removeJob(jobId);
      loadJobs();
      if (currentJobId === jobId) {
        onJobSelect(null);
      }
    } catch (error) {
      alert('Failed to delete job: ' + error.message);
    }
  };

  const handleCancelClick = (job, e) => {
    e.stopPropagation();
    setJobToCancel(job);
    setRunCleanupOnCancel(true);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!jobToCancel) return;
    
    setCancelling(true);
    try {
      const result = await apiService.cancelJob(jobToCancel.id, runCleanupOnCancel);
      if (result.success) {
        loadJobs();
        if (result.cleanupJobId) {
          alert(`Job cancelled. Cleanup job started: ${result.cleanupJobId}`);
        } else {
          alert('Job cancelled successfully');
        }
      } else {
        alert('Failed to cancel job: ' + result.message);
      }
    } catch (error) {
      alert('Failed to cancel job: ' + error.message);
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
      setJobToCancel(null);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getJobIcon = (state) => {
    const iconMap = {
      waiting: <Clock className="h-4 w-4 text-amber-500" />,
      active: <Play className="h-4 w-4 text-primary animate-pulse" />,
      completed: <CheckCircle className="h-4 w-4 text-green-500" />,
      failed: <XCircle className="h-4 w-4 text-destructive" />
    };
    return iconMap[state] || <Clock className="h-4 w-4" />;
  };

  const getJobTypeLabel = (job) => {
    return job.data?.type === 'uninstall' ? '🗑️' : '📦';
  };

  const renderJobSection = (title, jobList, state, badgeVariant) => {
    if (jobList.length === 0 && state !== 'active') return null;

    return (
      <Collapsible
        key={state}
        open={expandedSections[state]}
        onOpenChange={() => toggleSection(state)}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/50 rounded px-2 transition-colors">
          <div className="flex items-center gap-2">
            {expandedSections[state] ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {title}
            </span>
          </div>
          <Badge variant={badgeVariant} className="text-xs h-5 px-2">
            {jobList.length}
          </Badge>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1.5 mt-1">
          {jobList.length === 0 ? (
            <p className="text-xs text-muted-foreground pl-6 py-2">
              No {title.toLowerCase()} jobs
            </p>
          ) : (
            jobList.map((job) => (
              <div
                key={job.id}
                onClick={() => onJobSelect(job.id)}
                className={cn(
                  "p-2.5 rounded-lg cursor-pointer transition-all group",
                  "hover:bg-muted/50 border border-transparent",
                  currentJobId === job.id && "bg-primary/10 border-primary/50",
                  job.cancelled && "opacity-60"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getJobIcon(state)}
                    <span className="text-xs">{getJobTypeLabel(job)}</span>
                    <span className={cn(
                      "text-sm",
                      currentJobId === job.id && "font-semibold"
                    )}>
                      Job #{job.id}
                    </span>
                    {job.cancelled && (
                      <Badge variant="outline" className="text-xs h-4 px-1 text-amber-500 border-amber-500">
                        Cancelling
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {state === 'active' && !job.cancelled && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:bg-destructive/20"
                            onClick={(e) => handleCancelClick(job, e)}
                          >
                            <StopCircle className="h-3 w-3 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancel job</TooltipContent>
                      </Tooltip>
                    )}
                    {state === 'waiting' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleDeleteJob(job.id, e)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                    {(state === 'completed' || state === 'failed') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleDeleteJob(job.id, e)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1 pl-6">
                  {job.data?.nodes?.length || 0} nodes • {job.data?.version || 'N/A'} • {job.data?.useKRaft ? 'KRaft' : 'Legacy'}
                </p>
                {job.failedReason && (
                  <p className="text-xs text-destructive mt-1.5 pl-6 truncate">
                    ❌ {job.failedReason}
                  </p>
                )}
              </div>
            ))
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const totalJobs = jobs.active.length + jobs.waiting.length + jobs.completed.length + jobs.failed.length;

  return (
    <>
      <Card className="sticky top-16 border-border/50">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold">Job Queue</h3>
              <p className="text-xs text-muted-foreground">
                {totalJobs} job{totalJobs !== 1 ? 's' : ''} total
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={loadJobs}
                  disabled={loading}
                  className="h-7 w-7"
                >
                  <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh jobs</TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          {totalJobs === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Download className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium">No jobs yet</p>
              <p className="text-xs text-muted-foreground">
                Deploy a cluster to get started
              </p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[450px] overflow-y-auto">
              {renderJobSection('Active', jobs.active, 'active', 'default')}
              {renderJobSection('Waiting', jobs.waiting, 'waiting', 'warning')}
              {renderJobSection('Completed', jobs.completed, 'completed', 'success')}
              {renderJobSection('Failed', jobs.failed, 'failed', 'destructive')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Job Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Cancel Installation Job
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Are you sure you want to cancel this installation job? This action cannot be undone.
              </p>
              <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                <Checkbox 
                  id="cleanup" 
                  checked={runCleanupOnCancel}
                  onCheckedChange={setRunCleanupOnCancel}
                />
                <Label htmlFor="cleanup" className="text-sm cursor-pointer">
                  Run cleanup/uninstall on affected servers
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {runCleanupOnCancel 
                  ? "A cleanup job will be created to remove any partially installed components from the servers."
                  : "Servers may be left in an inconsistent state. Manual cleanup may be required."}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Running</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelConfirm}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? 'Cancelling...' : 'Cancel Job'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default JobStatusPanel;
